import { Form } from "antd";
import { Result } from "api/baseApiResponseModel/BaseApiResponseModel";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { strings } from "utils/localizedStrings";
import { IAppTransactionRepository } from "api/repositories/appTransaction/AppTransactionRepository";
import { AppTransactionResponseModel } from "api/repositories/appTransaction/models/AppTransactionResponseModel";
import { AppTransactionListRequestModel } from "api/repositories/appTransaction/models/AppTransactionListRequestModel";
import { AppTransactionDetailRequestModel } from "api/repositories/appTransaction/models/AppTransactionDetailRequestModel";

const AppPhoneRechargeViewModel = (repository: IAppTransactionRepository) => {
  const service = "AIR_TIME"
  const [filterForm] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [transList, setTransList] = useState<AppTransactionResponseModel[]>([]);
  const [transDetail, setTransDetail] = useState<AppTransactionResponseModel | null>(null);
  const [resultObject, setResultObject] = useState<Result | null>(null);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [query, setQuery] = useState<AppTransactionListRequestModel>({
    page: 0,
    limit: 10,
    service,
    fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    toDate: dayjs().format('YYYY-MM-DD'),
  });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [checkStatusLoading, setCheckStatusLoading] = useState<Record<string, boolean>>({});

  const onCheckStatus = async (code: string) => {
    try {
      setCheckStatusLoading({ ...checkStatusLoading, [code]: true });
      const res = await repository.checkStatus(code);
      if (res?.data) {
        setResultObject({ type: "success", message: res?.message });
        getTransactionList(query);
      }
    } catch (error) {
      console.error(error);
      setResultObject({ type: "error", message: error?.error?.message || strings.GlobalMessage.Error });
    } finally {
      setCheckStatusLoading({ ...checkStatusLoading, [code]: false });
    }
  }

  const getTransactionList = async (params: AppTransactionListRequestModel) => {
    try {
      setLoading(true);
      const res = await repository.getList(params);
      if (res?.data) {
        setTransList(res?.data?.data?.items);
        setTotal(res?.data?.pagination?.total);
        setLimit(res?.data?.pagination?.limit);
      }
    } catch (error: any) {
      console.error(error);
      setResultObject({ type: "error", message: error?.error?.message || strings.GlobalMessage.Error });
    } finally {
      setLoading(false);
    }
  }

  const getTransactionDetail = async (params: AppTransactionDetailRequestModel) => {
    try {
      setDetailLoading(true);
      const res = await repository.getDetail(params);
      if (res?.data) {
        setTransDetail(res?.data?.data);
      }
    } catch (error: any) {
      console.error(error);
      setResultObject({ type: "error", message: error?.error?.message || strings.GlobalMessage.Error });
    } finally {
      setDetailLoading(false);
    }
  }

  const exportReportTransaction = async (params: AppTransactionListRequestModel) => {
    try {
      setLoading(true);
      const res = await repository.exportReport(params);
      if (res?.data) {
        const url = window.URL.createObjectURL(res?.data as Blob);
        const link = document.createElement('a');
        const timestamp = dayjs().format('YYYYMMDD_HHmmss');
        link.href = url;
        link.setAttribute('download', `${service}_report_${timestamp}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setResultObject({ type: "success", message: strings.AppTransactionHistory.ExportSuccess });
      }
    } catch (error: any) {
      console.error(error);
      setResultObject({ type: "error", message: error?.error?.message || strings.GlobalMessage.Error });
    } finally {
      setLoading(false);
    }
  }

  const handleTableChange = (pagination?: any) => {
    setPage(pagination?.current - 1);
    setLimit(pagination?.pageSize);
    setQuery({
      ...query,
      page: pagination?.current - 1,
      limit: pagination?.pageSize
    });
  }

  const onDetailModalClose = () => {
    setShowModal(false);
  }

  useEffect(() => {
    getTransactionList(query);
  }, [query]);

  useEffect(() => {
    showModal && getTransactionDetail({ code: selectedItem });
  }, [showModal, selectedItem]);

  return {
    filterForm,
    loading,
    detailLoading,
    transList,
    transDetail,
    resultObject,
    page, setPage,
    limit, total,
    setQuery,
    showModal, setShowModal, onDetailModalClose,
    showExportModal, setShowExportModal,
    setSelectedItem,
    exportReportTransaction,
    handleTableChange, service,
    onCheckStatus, checkStatusLoading
  }
}

export default AppPhoneRechargeViewModel